import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * SVG illustrated talking head avatar.
 * Mouth syncs to amplitude, blinks randomly, subtle head bob.
 * Includes Simli.com stub for future photorealistic upgrade.
 */
export default function AIAvatar({ amplitude = 0, isSpeaking = false, isThinking = false, size = 64, provider = 'built-in' }) {
    const [blinking, setBlinking] = useState(false);
    const blinkTimer = useRef(null);

    // Random blink every 2-6 seconds
    useEffect(() => {
        const scheduleBlink = () => {
            const delay = 2000 + Math.random() * 4000;
            blinkTimer.current = setTimeout(() => {
                setBlinking(true);
                setTimeout(() => setBlinking(false), 150);
                scheduleBlink();
            }, delay);
        };
        scheduleBlink();
        return () => clearTimeout(blinkTimer.current);
    }, []);

    // Simli.com stub
    if (provider === 'simli') {
        return (
            <div
                className="rounded-full bg-gradient-to-br from-violet-100 to-violet-200 border-2 border-violet-300 flex items-center justify-center relative overflow-hidden"
                style={{ width: size, height: size }}
            >
                <Sparkles className="w-1/3 h-1/3 text-violet-500" />
                <div className="absolute bottom-0 inset-x-0 bg-violet-600/90 text-white text-center py-0.5" style={{ fontSize: size * 0.12 }}>
                    Coming Soon
                </div>
            </div>
        );
    }

    // Mouth shape based on amplitude
    const mouthOpen = amplitude > 0.5 ? 'open' : amplitude > 0.15 ? 'half' : 'closed';
    const headBob = isSpeaking ? Math.sin(Date.now() / 200) * 1.5 : 0;
    const s = size; // SVG viewBox scale

    return (
        <div
            style={{
                width: size, height: size,
                transform: `translateY(${headBob}px)`,
                transition: 'transform 0.1s ease-out',
            }}
        >
            <svg viewBox="0 0 100 100" width={size} height={size}>
                {/* Face circle */}
                <circle cx="50" cy="50" r="42" fill="url(#faceGrad)" stroke="#c4b5fd" strokeWidth="2" />

                {/* Gradient */}
                <defs>
                    <radialGradient id="faceGrad" cx="40%" cy="35%">
                        <stop offset="0%" stopColor="#ede9fe" />
                        <stop offset="100%" stopColor="#ddd6fe" />
                    </radialGradient>
                    <radialGradient id="eyeGrad">
                        <stop offset="0%" stopColor="#4c1d95" />
                        <stop offset="100%" stopColor="#1e1b4b" />
                    </radialGradient>
                </defs>

                {/* Eyes */}
                {blinking ? (
                    <>
                        <line x1="30" y1="42" x2="40" y2="42" stroke="#4c1d95" strokeWidth="2.5" strokeLinecap="round" />
                        <line x1="60" y1="42" x2="70" y2="42" stroke="#4c1d95" strokeWidth="2.5" strokeLinecap="round" />
                    </>
                ) : (
                    <>
                        <ellipse cx="35" cy="42" rx="5" ry={isThinking ? 4 : 5.5} fill="url(#eyeGrad)" />
                        <ellipse cx="65" cy="42" rx="5" ry={isThinking ? 4 : 5.5} fill="url(#eyeGrad)" />
                        {/* Eye highlights */}
                        <circle cx="33" cy="40" r="1.5" fill="white" opacity="0.8" />
                        <circle cx="63" cy="40" r="1.5" fill="white" opacity="0.8" />
                    </>
                )}

                {/* Eyebrows - raise when thinking */}
                <line
                    x1="27" y1={isThinking ? 31 : 33} x2="42" y2={isThinking ? 30 : 32}
                    stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"
                />
                <line
                    x1="58" y1={isThinking ? 30 : 32} x2="73" y2={isThinking ? 31 : 33}
                    stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"
                />

                {/* Mouth */}
                {mouthOpen === 'open' ? (
                    <ellipse cx="50" cy="62" rx="8" ry="6" fill="#7c3aed" opacity="0.7">
                        <animate attributeName="ry" values="5;7;5" dur="0.3s" repeatCount="indefinite" />
                    </ellipse>
                ) : mouthOpen === 'half' ? (
                    <ellipse cx="50" cy="62" rx="6" ry="3" fill="#7c3aed" opacity="0.6" />
                ) : (
                    <path d="M42 62 Q50 67 58 62" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
                )}

                {/* Cheek blush */}
                <circle cx="26" cy="55" r="6" fill="#c4b5fd" opacity="0.2" />
                <circle cx="74" cy="55" r="6" fill="#c4b5fd" opacity="0.2" />

                {/* Thinking dots */}
                {isThinking && (
                    <g opacity="0.6">
                        <circle cx="42" cy="62" r="1.5" fill="#7c3aed">
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0s" repeatCount="indefinite" />
                        </circle>
                        <circle cx="50" cy="62" r="1.5" fill="#7c3aed">
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0.3s" repeatCount="indefinite" />
                        </circle>
                        <circle cx="58" cy="62" r="1.5" fill="#7c3aed">
                            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0.6s" repeatCount="indefinite" />
                        </circle>
                    </g>
                )}
            </svg>
        </div>
    );
}
